import { zlozPdf } from '@/lib/export';
import type { Planogram } from '@/lib/planogram-api';
import {
  ETYKIETA_PT,
  LINIA_NAZWY_PT,
  LINII_NAZWY,
  nazwaWKomorce,
  odstepDoDolu,
  KOLOR_BRAKU,
  KOLOR_PUSTEJ,
  LEGENDA_SZER_PT,
  LEGENDA_WIERSZ_PT,
  MARGINES_PT,
  NAGLOWEK_KOLUMN_PT,
  ODSTEP_KOMORKI_PT,
  ODSTEP_POZIOMY_PT,
  legendaMapy,
  ukladBebna,
  wymiaryMapy,
  type TrybKoloru,
} from '@/lib/planogram-mapa';

/**
 * MAPA SZAFY JAKO DOKUMENT pdfmake.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️ FUNKCJA ZWRACA GOŁY OBIEKT, a składanie pliku robi `zlozPdf`. Dzięki temu układ da się
 *  sprawdzić testem (rozmiar strony, liczba wierszy, kolor konkretnej komórki) bez uruchamiania
 *  generatora PDF w środowisku testowym.
 *
 *  ⚠️ `pageSize` W pdfmake JEST NA CAŁY DOKUMENT, nie na stronę. Automat z dostawką ma bębny
 *  różnej szerokości, więc strona jest rozmiaru NAJWIĘKSZEGO z nich, a węższy bęben zostawia
 *  z prawej puste pole. Osobny plik na bęben byłby gorszy: mapa jednej szafy to jeden dokument.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export type OpcjeMapy = {
  dane: Planogram;
  tryb: TrybKoloru;
  /** Nazwa wyspy do nagłówka — `null`, gdy nieznana. */
  wyspa: string | null;
  teraz: Date;
  t: (klucz: string) => string;
};

/** Kolory kresek: segment ciemny i gruby, zwykła krawędź jasna. */
const KRESKA_SEGMENTU = '#1f2421';
const KRESKA = '#9aa0a6';

export function dokumentMapy({ dane, tryb, wyspa, teraz, t }: OpcjeMapy): Record<string, unknown> {
  const legenda = legendaMapy(dane.drawers, tryb);
  const poId = new Map(legenda.map((p) => [p.itemId, p]));

  const bebny = dane.summary.drums.map((beben) => {
    const uklad = ukladBebna(
      dane.drawers.filter((d) => d.picNumber === beben.picNumber),
      beben.rows,
    );
    const wLegendzie = legenda.filter((p) => p.wBebnach.has(beben.picNumber));
    return {
      beben,
      uklad,
      wLegendzie,
      wymiary: wymiaryMapy({
        miejsca: uklad.miejsca,
        kolumn: uklad.kolumny.length,
        wiersze: beben.rows,
        pozycjiLegendy: wLegendzie.length,
      }),
    };
  });

  const szerokosc = Math.max(...bebny.map((b) => b.wymiary.szerokosc));
  const wysokosc = Math.max(...bebny.map((b) => b.wymiary.wysokosc));

  const zajete = dane.drawers.reduce((s, d) => s + d.compartments.filter((c) => c.item !== null).length, 0);

  const content = bebny.flatMap(({ beben, uklad, wLegendzie, wymiary }, i) => {
    const { slotPt, wierszPt, zNazwami, legendaKolumn } = wymiary;

    /**
     * ⚠️ NAZWĘ PRZYCINAMY MY, bo pdfmake nie obcina tekstu w komórce — rozpycha wiersz.
     *
     * ⚠️ CZTERY PUNKTY NA ZNAK to zmierzona szerokość wersalików Roboto 7 pt (nazwy towarów
     * u klientów są pisane wielkimi literami). Optymistyczne 3,4 dawało trzecią linię w komórce.
     */
    const naZnakow = Math.max(6, Math.floor((slotPt - 6) / 4));

    const naglowek = [
      { text: '', border: [false, false, false, false] },
      ...uklad.kolumny.map((k) => ({
        text: k.span > 1 ? `${k.drawerNo} ×${k.span}` : String(k.drawerNo),
        alignment: 'center',
        fontSize: 8,
        bold: true,
        color: '#3a3a3a',
      })),
    ];

    const wiersze = Array.from({ length: beben.rows }, (_, w) => [
      { text: String(w + 1), alignment: 'right', fontSize: 8, color: '#6b7280', border: [false, false, false, false] },
      ...uklad.kolumny.map((k) => {
        const c = k.komorki[w] ?? null;
        /** TRZY STANY, nie dwa — „nie ma wiersza" to informacja o geometrii, „pusta" o zapasie. */
        if (c === null) {
          return { text: t('planogram.mapaBrakWiersza'), fillColor: KOLOR_BRAKU, fontSize: 6, color: '#6b7280' };
        }
        if (c.item === null) {
          return { text: t('planogram.emptySlot'), fillColor: KOLOR_PUSTEJ, fontSize: 6.5, color: '#6b7280' };
        }
        const p = poId.get(c.item.id);
        const nr = String(p?.nr ?? '?');
        /** Same NIEPUSTE linie — puste i tak nie dojadą do pliku, patrz margines dolnego wiersza. */
        const linieNazwy = nazwaWKomorce(c.item.name, naZnakow).filter((l) => l !== '');
        const stan = `${c.qtyCurrent ?? '?'}/${c.qtyMax ?? '?'} ${t('planogram.mapaSzt')}`;
        return {
          fillColor: p?.kolor ?? KOLOR_PUSTEJ,
          /**
           * ⚠️ PRZY WĄSKIM MIEJSCU ZOSTAJE NUMER I STAN, a nazwa idzie do legendy. Wciśnięta
           * nazwa nie jest „mniej czytelna", tylko nieczytelna — a numer odsyła do legendy,
           * więc informacja nie ginie.
           */
          stack: zNazwami
            ? [
                /**
                 * ⚠️ NAZWA ŁAMANA PRZEZ NAS, nie przez pdfmake — patrz `nazwaWKomorce`. Liczba
                 * zajętych linii wchodzi niżej do marginesu dolnego wiersza.
                 */
                { text: linieNazwy.join('\n'), fontSize: 7, lineHeight: 1.05 },
                /**
                 * ⚠️ NUMER W PRAWYM DOLNYM ROGU (zgłoszone 02.09.2026, poprawiane tego samego
                 * dnia dwa razy). Stał przed nazwą i tam przeszkadzał: nazwa jest tym, czego
                 * człowiek szuka wzrokiem, a numer służy dopiero do zejścia do legendy. Odstęp
                 * od góry dopycha ten wiersz do dolnej krawędzi — pdfmake nie zna wyrównania
                 * do dołu komórki.
                 *
                 * ⚠️⚠️ DOLICZAMY LINIE, KTÓRYCH NAZWA NIE ZAJĘŁA. Pierwsze podejście wypełniało
                 * drugą linię spacją, żeby blok nazwy miał zawsze tę samą wysokość — pdfmake
                 * LINIĘ Z SAMEJ SPACJI WYRZUCA, więc przegródki z krótką nazwą miały numer wyżej
                 * o dokładnie jedną linię (zmierzone na złożonym pliku: 350 komórek na 15,5 pt
                 * od dołu i 34 na 6,9 pt — dokładnie tak, jak to zgłoszono: „raz wyżej raz
                 * niżej"). Liczba linii jest nam znana, więc dopychamy marginesem, zamiast
                 * liczyć na to, co biblioteka zrobi z białym znakiem.
                 */
                {
                  margin: [
                    0,
                    Math.round(odstepDoDolu(wierszPt) + (LINII_NAZWY - linieNazwy.length) * LINIA_NAZWY_PT),
                    0,
                    0,
                  ],
                  columns: [
                    { text: stan, fontSize: 6.5 },
                    { text: ` ${nr} `, width: 'auto', bold: true, fontSize: 7.5, background: '#ffffff' },
                  ],
                },
              ]
            : [
                { text: nr, bold: true, fontSize: 8, alignment: 'center' },
                { text: stan, fontSize: 6, alignment: 'center' },
              ],
        };
      }),
    ]);

    const legendaWiersze: unknown[][] = [];
    for (let od = 0; od < wLegendzie.length; od += legendaKolumn) {
      const paczka = wLegendzie.slice(od, od + legendaKolumn);
      const wiersz: unknown[] = [];
      for (const p of paczka) {
        const wBebnie = p.wBebnach.get(beben.picNumber)!;
        wiersz.push(
          { text: String(p.nr), fillColor: p.kolor, alignment: 'center', fontSize: 8, bold: true },
          {
            stack: [
              { text: p.nazwa, fontSize: 7.5 },
              {
                text: [
                  t('planogram.mapaWLegendzie')
                    .replace('{n}', String(wBebnie.przegrodek))
                    .replace('{m}', String(wBebnie.maks)),
                  tryb === 'grupa' ? ` · ${p.grupa ?? t('planogram.mapaBezGrupy')}` : '',
                ].join(''),
                fontSize: 6.5,
                color: '#6b7280',
              },
            ],
          },
        );
      }
      /** Ostatni wiersz legendy dopełniamy pustymi komórkami — tabela musi być prostokątna. */
      while (wiersz.length < legendaKolumn * 2) wiersz.push({ text: '' });
      legendaWiersze.push(wiersz);
    }

    return [
      {
        text: `${dane.machine.name} (VMID ${dane.machine.vmid})${wyspa === null ? '' : ` · ${wyspa}`}`,
        fontSize: 14,
        bold: true,
        ...(i > 0 ? { pageBreak: 'before' } : {}),
      },
      {
        text: `${beben.label} · ${t('planogram.drum').replace('{n}', String(beben.picNumber))}`,
        fontSize: 9,
        margin: [0, 2, 0, 0],
      },
      {
        text: t('planogram.mapaWygenerowano')
          .replace('{data}', formatChwili(teraz))
          .replace('{t}', String(legenda.length))
          .replace('{p}', String(zajete)),
        fontSize: 8,
        color: '#6b7280',
        margin: [0, 2, 0, 0],
      },
      /**
       * ⚠️ LINIA KONWENCJI USUNIĘTA 02.09.2026 na wprost zgłoszone życzenie („wywal też z pdf ten
       * cały opis"). Trzy rzeczy, które tłumaczyła, i tak są widoczne w rysunku: numery kolumn
       * z dopiskiem `×N` stoją w nagłówku, grubsza kreska jest grubsza, a układ osi wynika
       * z tego, że kolumny są podpisane u góry. Uzasadnienie samego segmentu (założenie o trzech
       * miejscach) zostaje w kodzie, przy `MIEJSC_W_SEGMENCIE`.
       */
      {
        table: {
          headerRows: 1,
          /**
           * ⚠️ WYMIARY POMNIEJSZONE O ODSTĘPY KOMÓRKI — pdfmake dodaje je z zewnątrz, więc dopiero
           * po odjęciu miejsce bębna ma dokładnie `SLOT_PT`, a granica segmentu wypada co trzy
           * miejsca. Bez tego siatka 54-miejscowa wychodziła za prawy brzeg strony. Uzasadnienie
           * i pomiar stoją przy `ODSTEP_POZIOMY_PT`.
           */
          widths: [
            ETYKIETA_PT - 2 * ODSTEP_POZIOMY_PT,
            ...uklad.kolumny.map((k) => k.spanRysowany * slotPt - 2 * ODSTEP_POZIOMY_PT),
          ],
          heights: (wiersz: number): number =>
            (wiersz === 0 ? NAGLOWEK_KOLUMN_PT : wierszPt) - 2 * ODSTEP_KOMORKI_PT,
          body: [naglowek, ...wiersze],
        },
        layout: {
          hLineWidth: (): number => 0.75,
          /** Gruba pionowa kreska = granica segmentu („blachy"). Patrz `MIEJSC_W_SEGMENCIE`. */
          vLineWidth: (kreska: number): number => (uklad.grubeKrawedzie.has(kreska) ? 2.5 : 0.75),
          hLineColor: (): string => KRESKA,
          vLineColor: (kreska: number): string => (uklad.grubeKrawedzie.has(kreska) ? KRESKA_SEGMENTU : KRESKA),
          paddingLeft: (): number => ODSTEP_POZIOMY_PT,
          paddingRight: (): number => ODSTEP_POZIOMY_PT,
          paddingTop: (): number => ODSTEP_KOMORKI_PT,
          paddingBottom: (): number => ODSTEP_KOMORKI_PT,
        },
      },
      { text: t('planogram.mapaLegenda'), fontSize: 8, bold: true, margin: [0, 10, 0, 4] },
      legendaWiersze.length === 0
        ? { text: t('planogram.mapaLegendaPusta'), fontSize: 7.5, color: '#6b7280' }
        : {
            table: {
              widths: Array.from({ length: legendaKolumn }, () => [16, LEGENDA_SZER_PT - 22]).flat(),
              heights: (): number => LEGENDA_WIERSZ_PT - 8,
              body: legendaWiersze,
            },
            layout: {
              hLineWidth: (): number => 0,
              vLineWidth: (): number => 0,
              paddingLeft: (): number => 2,
              paddingRight: (): number => 4,
              paddingTop: (): number => 1,
              paddingBottom: (): number => 1,
            },
          },
    ];
  });

  return {
    pageSize: { width: szerokosc, height: wysokosc },
    pageMargins: [MARGINES_PT, MARGINES_PT, MARGINES_PT, MARGINES_PT],
    defaultStyle: { font: 'Roboto', fontSize: 8, color: '#1f2421' },
    content,
  };
}

/** `2026-09-02 15:32` — bez sekund i bez strefy, bo to podpis wydruku, nie znacznik czasu. */
function formatChwili(d: Date): string {
  const dwa = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${dwa(d.getMonth() + 1)}-${dwa(d.getDate())} ${dwa(d.getHours())}:${dwa(d.getMinutes())}`;
}

export async function zlozMapePdf(opcje: OpcjeMapy): Promise<Blob> {
  return zlozPdf(dokumentMapy(opcje));
}
